import React, {useContext, useEffect, useState} from "react";
import {Checkbox, Col, Divider, Form, Input, Row, Select} from "antd";
import CodeInput from "./CodeInput";
import {PAYMENT_SUCCESS} from "./AccountSettings";
import {useGoogleReCaptcha} from "react-google-recaptcha-v3";
import {UserContext} from "@context/UserContext";
import Alert from "@common/Alert";
import CircularLoader from "@common/CircularLoader";
import Button from "@common/Button";

const AccountSettingsRenewProductCardPayment = props => {
    const {data, checkFormStatus, handleSubmitForm} = props;
    const [product, setProduct] = useState(null);
    const [paymentError, setPaymentError] = useState(null);
    const [submittable, setSubmittable] = useState(false);
    const [disabled, setDisabled] = useState(false);
    const [form] = Form.useForm();
    const {user} = useContext(UserContext);
    const {executeRecaptcha} = useGoogleReCaptcha();

    const handleProductChange = (productId) => {
        setProduct(productId ? (data?.cardProducts ?? []).find(product => product.productId === productId) : null);
    }

    const handleSubmitFormWrapper = async (values) => {
        form.setFields(Object.keys(form.getFieldsValue()).map(name => ({name, errors: []})));

        try {
            const validatedValues = await form.validateFields();

            if (!executeRecaptcha) {
                console.warn("reCAPTCHA not ready");
                return;
            }

            setDisabled(true);
            setPaymentError(null);
            try {
                const token = await executeRecaptcha("submit");

                if (!token) {
                    form.setFields([{name: "recaptcha", errors: ["Failed to verify reCAPTCHA"]}]);
                    setDisabled(false);
                    return;
                }

                const formData = {...validatedValues, recaptchaToken: token};
                handleSubmitForm(form, formData, {}, "/web/ui/accountSettings/buyProduct", () => {
                    window.location.href = `${window.location.pathname}?${PAYMENT_SUCCESS}`;
                }, (error) => {
                    setDisabled(false);
                    setPaymentError(error.form);
                });
            } catch (error) {
                form.setFields([{name: "recaptcha", errors: ["Error verifying reCAPTCHA"]}]);
                setDisabled(false);
            }
        } catch (error) {
            console.error("Validation failed:", error);
        }
    };

    useEffect(() => {
        const observer = new MutationObserver((mutationsList, observer) => {
            for (let mutation of mutationsList) {
                if (mutation.type === 'childList') {
                    const recaptchaBadge = document.getElementsByClassName('grecaptcha-badge');
                    if (recaptchaBadge.length > 0) {
                        recaptchaBadge[0].style.bottom = '140px';
                        observer.disconnect();
                    }
                }
            }
        });
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }, []);

    useEffect(() => {
        const userEmail = user.email;
        if (userEmail && userEmail != 'noemail@hotmaths.com.au') {
            form.setFieldValue('cardEmail', userEmail);
        }
    }, [user]);

    return (
        <Form
            form={form}
            layout="vertical"
            onFieldsChange={() => setSubmittable(checkFormStatus(form))}
            onFinish={handleSubmitFormWrapper}
            initialValues={{
                terms: false,
                recaptcha: true
            }}
        >
            <Form.Item
                label="Product"
                name="productId"
                rules={[{required: true, message: 'Select a product'}]}
                validateTrigger="onBlur"
            >
                <Select onChange={handleProductChange}>
                    {data?.cardProducts.map(type => {
                        return (
                            <Select.Option key={type.productId} value={type.productId}>
                                {type.name}
                            </Select.Option>
                        );
                    })}
                </Select>
            </Form.Item>
            { product && (
                <p>Subscription fee: AUD ${product.fee / 100} <em>(incl. GST for Australian customers)</em></p>
            )}
            <Divider/>
            <Form.Item
                label="Card Type"
                name="cardType"
                aria-required="true"
                rules={[{required: true, message: 'Select your card type'}]}
                validateTrigger="onBlur"
            >
                <Select>
                    {data?.cardTypes.map(type => {
                        return (
                            <Select.Option key={type} value={type}>
                                {type}
                            </Select.Option>
                        );
                    })}
                </Select>
            </Form.Item>
            <Form.Item
                label="Name On Card"
                name="cardName"
                rules={[{required: true, message: 'Please enter the card name'}]}
                validateTrigger="onBlur"
            >
                <Input/>
            </Form.Item>
            <Form.Item
                label="Card Number"
                name="cardNumber"
                rules={[
                    {required: true, message: 'Please enter the card number'},
                    {
                        validator: (_, value) =>
                            value && value.replace(/\s/g, "").length === 16
                                ? Promise.resolve()
                                : Promise.reject("Card number must be 16 digits"),
                    }
                ]}
                validateTrigger="onBlur"
            >
                <CodeInput
                    allowedCharacters={/[^0-9]/gi}
                    delimiter=" "
                    segmentLength={4}
                    totalSegments={4}
                />
            </Form.Item>
            <Row gutter={8}>
                <Col span={8}>
                    <Form.Item
                        label="Expiry Date"
                        name="cardExpiry"
                        rules={[{required: true, message: 'Please enter the expiry date'}]}
                        validateTrigger="onBlur"
                    >
                        <CodeInput
                            allowedCharacters={/[^0-9]/gi}
                            delimiter="/"
                            segmentLength={2}
                            totalSegments={2}
                            placeholder="MM/YY"
                            outputWithDelimiter={true}
                        />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item
                        label="CVV"
                        name="cvv"
                        rules={[{required: true, message: 'Please enter the CVV'}]}
                        validateTrigger="onBlur"
                    >
                        <Input maxLength={3}/>
                    </Form.Item>
                </Col>
            </Row>
            <Form.Item
                label="Recipt email"
                name="cardEmail"
                rules={[
                    {required: true, message: "Email is required"},
                    {type: "email", message: "Invalid email format"}
                ]}
                validateTrigger="onBlur"
            >
                <Input
                    aria-required="true"
                />
            </Form.Item>
            <Form.Item
                name="recaptcha"
                rules={[{required: true, message: 'Please complete the reCAPTCHA'}]}
                validateTrigger="onBlur"
            >
                <p>ReCAPTCHA verification will be performed upon form submission.</p>
            </Form.Item>
            <Form.Item
                name="terms"
                valuePropName="checked"
                rules={[
                    {
                        validator: (_, value) =>
                            value ? Promise.resolve() : Promise.reject(new Error('Please accept the terms and conditions')),
                    }
                ]}
                validateTrigger="onBlur"
            >
                <Checkbox>I accept the terms and conditions</Checkbox>
            </Form.Item>
            {paymentError && (
                <Alert
                    className="margin-y--l"
                    showIcon
                    type="error"
                    description={paymentError}
                />
            )}
            <div className="flexAlignCentre">
                <Form.Item className="flex margin-y--rm margin-r--m">
                    <Button type="primary" htmlType="submit" disabled={disabled ? disabled : !submittable}>
                        Purchase
                    </Button>
                </Form.Item>
                {disabled && (
                    <CircularLoader pageCentered={false} displayInElement={true} spacing={false} />
                )}
            </div>
        </Form>
    )
}

export default AccountSettingsRenewProductCardPayment;